/**
 * CalendarDayView - Unified calendar day view component
 * 
 * Used by both authenticated (CalendarDetailPage) and public (PublicCalendarPage) contexts.
 * Changes made here will reflect in both views.
 * 
 * @module components/calendar/CalendarDayView
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Clock, 
  MapPin, 
  Building,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  StickyNote,
  Share2
} from 'lucide-react';
import Chip from '../Chip';
import EventSourceBadge, { EventAttribution } from './EventSourceBadge';
import EventWorkspaceSelectionModal from '../EventWorkspaceSelectionModal';
import EventDetailsModal from './EventDetailsModal';
import { ViewNotesModal } from '../notes';
import { useEntityNotes, useEntityNoteCounts, noteCountFor, type NoteCountsResult } from '../../hooks/useNotesV2';
import SponsorDisplay from '../SponsorDisplay';
import AddToCalendarDropdown from './AddToCalendarDropdown';
import { StandardTable, StandardTableHeader } from '../StandardTable';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import { 
  CalendarEventDisplay,
  formatTimeDisplay,
  formatTimeStringForDisplay,
  getChamberBadge,
  isCommitteeEvent
} from '../../utils/calendarUtils';

// ============================================================================
// Types
// ============================================================================

export interface CalendarDayViewProps {
  /** Calendar events to display */
  events: CalendarEventDisplay[];
  /** View mode: 'card' for event cards, 'table' for bill-centric table */
  viewMode: 'card' | 'table';
  /** If true, uses public bill/legislator links and hides workspace features */
  isPublic?: boolean;
  /** Workspace ID - if provided, enables workspace features (notes, tracked bills) */
  workspaceId?: string;
  /** Scope for public calendars - 'all', 'org', or 'ws' */
  scope?: 'all' | 'org' | 'ws';
  /** Callback when a bill is clicked (for authenticated view navigation) */
  onBillClick?: (billNumber: string) => void;
  /** Day data with full event details (for bill-centric table) */
  dayData?: {
    date: string;
    events: any[];
  };
  /** Show Date column - used for week/month table views */
  showDateColumn?: boolean;
}

// Helper to copy public event URL to clipboard
const copyEventShareLink = (eventId: number) => {
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/c/e/${eventId}`;
  navigator.clipboard.writeText(shareUrl).then(() => {
    toast.success('Event link copied to clipboard');
  }).catch(() => {
    toast.error('Failed to copy link');
  });
};

type SortField = 'time' | 'billNumber' | 'title' | 'room' | 'location' | 'status' | 'date';
type SortDirection = 'asc' | 'desc';

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Calendar-only notes button - for events without tracked bills
 * Shows only calendar entity notes, not bill notes
 */
/*
  Note counts for every event and tracked bill on screen, fetched once at the
  day-view level (#1179). Before this, each event's notes button issued one
  GET /entities/.../notes?limit=100 per tracked bill plus one for the event —
  hooks inside a .map() — just to render a number. Buttons read their counts
  from here and fetch the actual notes only when their modal opens.
*/
interface CalendarNoteCounts {
  bill?: NoteCountsResult;
  calendar?: NoteCountsResult;
}
const CalendarNoteCountsContext = React.createContext<CalendarNoteCounts | null>(null);

const CalendarOnlyNotesButton: React.FC<{
  calendarId: string;
  calendarDisplayName: string;
  workspaceId: string;
}> = ({ calendarId, calendarDisplayName, workspaceId }) => {
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const counts = React.useContext(CalendarNoteCountsContext);
  const calBatch = noteCountFor(counts?.calendar, calendarId);
  
  // Get calendar notes only
  const { notes: calendarNotes = [] } = useEntityNotes('calendar', calendarId, workspaceId, 100, calBatch === undefined || showNotesModal);
  
  const totalNotes = typeof calBatch === 'number' ? calBatch : calendarNotes.length;
  const hasActualNotes = totalNotes > 0;
  
  // Format notes for ViewNotesModal
  const formattedNotes = calendarNotes.map(note => ({
    id: note.id,
    title: note.title ? `[Calendar Event] ${note.title}` : '[Calendar Event]',
    content: note.content,
    created_at: note.created_at,
    updated_at: note.updated_at || note.created_at,
    include_in_reports: note.include_in_reports ?? true,
    author_name: note.author_name || 'Unknown',
    tags: note.tags || []
  }));
  
  return (
    <>
      <button
        onClick={() => setShowNotesModal(true)}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
          hasActualNotes
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
            : 'text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
        }`}
        title={`${totalNotes} note${totalNotes === 1 ? '' : 's'}`}
      >
        <StickyNote className="w-3.5 h-3.5" />
        <span className="font-medium">{totalNotes}</span>
      </button>
      
      {showNotesModal && (
        <ViewNotesModal
          isOpen={true}
          onClose={() => setShowNotesModal(false)}
          notes={formattedNotes}
          entityType="calendar"
          entityId={calendarId}
          entityDisplayName={calendarDisplayName}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

/**
 * Notes button for cards - shows combined bill + calendar notes
 */
const EventNotesButton: React.FC<{
  trackedBills: any[];
  calendarId: string;
  calendarDisplayName: string;
  workspaceId: string;
}> = ({ trackedBills, calendarId, calendarDisplayName, workspaceId }) => {
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const counts = React.useContext(CalendarNoteCountsContext);
  const batchActive = counts?.calendar !== undefined;
  // Badge number from the batch: event notes plus each tracked bill's notes.
  // (A note attached to both the event and a bill counts once per entity here;
  // the modal below lists the real notes once the user opens it.)
  const batchTotal = (() => {
    const c = noteCountFor(counts?.calendar, calendarId);
    let total = typeof c === 'number' ? c : 0;
    for (const bill of trackedBills) {
      const b = noteCountFor(counts?.bill, String(bill.bill_db_id || bill.id || ''));
      if (typeof b === 'number') total += b;
    }
    return total;
  })();
  // Only fetch the note bodies when there is no batch or the modal is open.
  const fetchNotes = !batchActive || showNotesModal;
  
  // Get calendar notes
  const { notes: calendarNotes = [] } = useEntityNotes('calendar', calendarId, workspaceId, 100, fetchNotes);
  
  // Get notes for all tracked bills
  // Note: This pattern violates Rules of Hooks but is necessary for the current architecture.
  // TODO: Refactor to use a single API call to fetch all bill notes at once.
  const billNotesQueries = trackedBills.map(bill => {
    const billDbId = bill.bill_db_id || bill.id;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useEntityNotes('bill', billDbId?.toString() || '', workspaceId, 100, fetchNotes);
  });
  
  // Combine all notes into a single array with source info
  const allNotes = React.useMemo(() => {
    const combined: any[] = [];
    
    // Add calendar notes with source
    calendarNotes.forEach(note => {
      combined.push({
        ...note,
        _source: 'calendar',
        _sourceDisplay: 'Calendar Event'
      });
    });
    
    // Add bill notes with source
    trackedBills.forEach((bill, index) => {
      const billNotes = billNotesQueries[index]?.notes || [];
      const billNumber = bill.bill_id || bill.number;
      billNotes.forEach(note => {
        combined.push({
          ...note,
          _source: 'bill',
          _sourceDisplay: billNumber
        });
      });
    });
    
    // Sort by created_at descending (most recent first)
    return combined.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [calendarNotes, billNotesQueries, trackedBills]);
  
  const totalNotes = batchActive ? batchTotal : allNotes.length;
  const hasActualNotes = totalNotes > 0;
  
  // Format notes for ViewNotesModal
  const formattedNotes = allNotes.map(note => ({
    id: note.id,
    title: note.title ? `[${note._sourceDisplay}] ${note.title}` : `[${note._sourceDisplay}]`,
    content: note.content,
    created_at: note.created_at,
    updated_at: note.updated_at || note.created_at,
    include_in_reports: note.include_in_reports ?? true,
    author_name: note.author_name || 'Unknown',
    tags: note.tags || []
  }));
  
  return (
    <>
      <button
        onClick={() => setShowNotesModal(true)}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
          hasActualNotes
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
            : 'text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
        }`}
        title={`${totalNotes} note${totalNotes === 1 ? '' : 's'}`}
      >
        <StickyNote className="w-3.5 h-3.5" />
        <span className="font-medium">{totalNotes}</span>
      </button>
      
      {showNotesModal && (
        <ViewNotesModal
          isOpen={true}
          onClose={() => setShowNotesModal(false)}
          notes={formattedNotes}
          entityType="calendar"
          entityId={calendarId}
          entityDisplayName={calendarDisplayName}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

/**
 * Compact notes display for table rows
 */
const TableNotesButton: React.FC<{
  billId: string;
  billNumber?: string;
  billTitle?: string;
  calendarId: string;
  calendarDisplayName?: string;
  workspaceId?: string;
}> = ({ billId, billNumber, billTitle, calendarId, calendarDisplayName, workspaceId }) => {
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const counts = React.useContext(CalendarNoteCountsContext);
  const batchActive = counts?.calendar !== undefined;
  const billBatch = noteCountFor(counts?.bill, billId);
  const calBatch = noteCountFor(counts?.calendar, calendarId);
  const batchTotal = (typeof billBatch === 'number' ? billBatch : 0) + (typeof calBatch === 'number' ? calBatch : 0);
  const fetchNotes = !batchActive || showNotesModal;
  const { notes: billNotes = [] } = useEntityNotes('bill', billId, workspaceId, 100, fetchNotes);
  const { notes: calendarNotes = [] } = useEntityNotes('calendar', calendarId, workspaceId, 100, fetchNotes);
  
  // Combine notes with source info, deduplicating by ID
  const allNotes = React.useMemo(() => {
    const notesMap = new Map();
    
    billNotes.forEach(note => {
      if (!notesMap.has(note.id)) {
        notesMap.set(note.id, {
          ...note,
          _source: 'bill',
          _sourceDisplay: billNumber || 'Bill'
        });
      }
    });
    
    calendarNotes.forEach(note => {
      if (!notesMap.has(note.id)) {
        notesMap.set(note.id, {
          ...note,
          _source: 'calendar',
          _sourceDisplay: calendarDisplayName || 'Calendar Event'
        });
      }
    });
    
    return Array.from(notesMap.values()).sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [billNotes, calendarNotes, billNumber, calendarDisplayName]);
  
  const totalCount = batchActive ? batchTotal : allNotes.length;
  
  const formattedNotes = allNotes.map(note => ({
    id: note.id,
    title: note.title ? `[${note._sourceDisplay}] ${note.title}` : `[${note._sourceDisplay}]`,
    content: note.content,
    created_at: note.created_at,
    updated_at: note.updated_at || note.created_at,
    include_in_reports: note.include_in_reports ?? true,
    author_name: note.author_name || 'Unknown',
    tags: note.tags || []
  }));
  
  return (
    <>
      <button
        onClick={() => setShowNotesModal(true)}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
          totalCount > 0
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
            : 'text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
        }`}
        title={`${totalCount} note${totalCount === 1 ? '' : 's'}`}
      >
        <StickyNote className="w-3.5 h-3.5" />
        <span className="font-medium">{totalCount}</span>
      </button>
      
      {showNotesModal && (
        <ViewNotesModal
          isOpen={true}
          onClose={() => setShowNotesModal(false)}
          notes={formattedNotes}
          entityType="bill"
          entityId={billId}
          entityDisplayName={`${billNumber || 'Unknown'} - ${billTitle || 'No title'}`}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

/**
 * Linkify bill numbers in description text
 */
const linkifyBillNumbers = (htmlText: string, isPublic: boolean, _onBillClick?: (billNumber: string) => void): React.ReactNode => {
  if (!htmlText) return null;
  
  // Convert newlines to <br> tags for proper HTML rendering
  let processedHtml = htmlText.replace(/\n/g, '<br/>');
  
  // Pattern matches: HB, SB, HR, SR, HJR, SJR, HCR, SCR, HM, SM
  // No trailing \b because bill numbers may run directly into text (e.g., "HB26-1045Clifford")
  // Capture optional following letter to know if we need to add a space
  const billPattern = /\b([HS](?:B|R|JR|CR|M))\s*(\d{2})-(\d{3,4})([A-Za-z])?/gi;
  
  processedHtml = processedHtml.replace(billPattern, (_match, prefix, year, number, followingChar) => {
    // Normalize the bill number (remove any spaces between parts)
    const normalizedBill = `${prefix}${year}-${number}`.toUpperCase();
    const linkHref = isPublic ? `/public/bills/${normalizedBill}` : `/legislation/${normalizedBill}`;
    const link = `<a href="${linkHref}" class="text-primary-600 dark:text-primary-400 hover:underline font-medium">${normalizedBill}</a>`;
    // Add space before following text if bill was directly adjacent to a letter
    return followingChar ? `${link} ${followingChar}` : link;
  });
  
  return <span dangerouslySetInnerHTML={{ __html: processedHtml }} />;
};

// ============================================================================
// Card View Component
// ============================================================================

const CardView: React.FC<{
  events: CalendarEventDisplay[];
  isPublic: boolean;
  workspaceId?: string;
  scope?: 'all' | 'org' | 'ws';
  onBillClick?: (billNumber: string) => void;
}> = ({ events, isPublic, workspaceId, scope, onBillClick }) => {
  // Sort events by chamber first (Senate, then House), then by display_order (source order)
  // display_order captures the exact order from the legislative calendar, including
  // where "Upon Adjournment" events appear relative to timed events
  // Floor work events have lower display_order values than committee events
  const sortedEvents = [...events].sort((a, b) => {
    // Chamber first: Senate before House (matching bill sorting convention)
    const chamberA = (a.chamber || '').toLowerCase();
    const chamberB = (b.chamber || '').toLowerCase();
    if (chamberA.includes('senate') && !chamberB.includes('senate')) return -1;
    if (!chamberA.includes('senate') && chamberB.includes('senate')) return 1;
    
    // Within chamber, use display_order (source order from legislative calendar)
    // Floor work has display_order 0, 1, 2...; committees have higher values
    const displayOrderA = (a as any).display_order ?? 999;
    const displayOrderB = (b as any).display_order ?? 999;
    return displayOrderA - displayOrderB;
  });

  // Get bill link path based on context
  const getBillLink = (billNumber: string) => {
    return isPublic 
      ? `/public/bills/${encodeURIComponent(billNumber)}`
      : `/legislation/${encodeURIComponent(billNumber)}`;
  };

  // Track-into-workspace picker (PRD §5.4b) — authenticated surfaces only
  const [trackingEvent, setTrackingEvent] = React.useState<{ key: string; title: string } | null>(null);
  const [detailsEvent, setDetailsEvent] = React.useState<any | null>(null);

  return (
    <div className="space-y-4">
      {sortedEvents.map((event) => {
        // Non-legislative events (election milestones, public political events,
        // workspace events — PRD §5.1) render a compact card: no chamber, no
        // bills sections. Public pages never receive these, so isPublic paths
        // are unaffected.
        const eventSource = (event as any).event_source as
          | 'legislative' | 'election' | 'public' | 'workspace' | undefined;
        if (eventSource && eventSource !== 'legislative') {
          const authored = event as any;
          const location = authored.derived_location
            || [authored.venue, authored.address, authored.city].filter(Boolean).join(', ');
          return (
            <div key={authored.event_key} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <EventSourceBadge source={eventSource} workspaceName={authored.workspace_name} />
                    {authored.event_type && (
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary capitalize">
                        {String(authored.event_type).replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">
                    {authored.reading_title || authored.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {authored.all_day ? 'All day' : formatTimeDisplay(event) || '—'}
                    </div>
                    {location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {location}
                      </div>
                    )}
                    {authored.format === 'virtual' && !location && <span>Virtual</span>}
                    {authored.host_name && <span>{authored.host_name}</span>}
                  </div>
                  {authored.calendar_description && (
                    <p className="mt-2 text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                      {authored.calendar_description}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-center gap-3">
                    {authored.link_url && (
                      <a
                        href={authored.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:underline"
                      >
                        Event link →
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setDetailsEvent(authored)}
                      className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:underline"
                    >
                      Details
                    </button>
                    {!isPublic && eventSource === 'public' && authored.event_key && (
                      <button
                        type="button"
                        onClick={() => setTrackingEvent({ key: authored.event_key, title: authored.reading_title || authored.title || '' })}
                        className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:underline"
                      >
                        {authored.is_tracked ? 'Tracked ✓' : 'Track'}
                      </button>
                    )}
                    <EventAttribution attribution={authored.attribution} />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        const chamberInfo = getChamberBadge(event);
        const timeDisplay = formatTimeDisplay(event);
        const hasCalendarId = event.calendar_id != null;

        // Determine if this is a scoped view (workspace or org)
        const isScopedView = scope === 'ws' || scope === 'org';
        const showTrackedSection = !isPublic || isScopedView;
        
        // For authenticated view with workspace, use tracked_bills from backend
        // For public scoped views, filter bills by is_tracked flag
        // tracked_bills array is populated by the backend's calendarService
        const trackedBillsData = workspaceId 
          ? (event as any).tracked_bills || []  // Use tracked_bills directly (may be empty)
          : isPublic && isScopedView && event.bills
          ? event.bills.filter((b: any) => b.is_tracked)
          : [];
        
        // For workspace views (authenticated or public scoped), show only tracked bills
        // For non-workspace views, show all bills
        const hasWorkspaceContext = !!workspaceId || isScopedView;
        const trackedBillsToShow = hasWorkspaceContext 
          ? trackedBillsData  // Only tracked bills for workspace context
          : event.bills || [];
        
        const sectionTitle = (workspaceId || isScopedView) ? 'Tracked Bills' : 'Bills';
        const isOrgScope = scope === 'org';
        const allBillNumbers: string[] = (event as any).bill_numbers || event.bills?.map((b) => b.number) || [];

        return (
          <div
            key={event.calendar_id ?? (event as any).event_key ?? `${event.reading_date}-${event.reading_title}`}
            className="card hover:shadow-lg transition-shadow"
          >
            {/* Bills Section - At top for workspace/org scoped views (including public) */}
            {/* For dashboard (non-scoped), only show if there are bills to display */}
            {showTrackedSection && (isScopedView || trackedBillsToShow.length > 0) && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {sectionTitle} {trackedBillsToShow.length > 0 && `(${trackedBillsToShow.length})`}
                  </h4>
                </div>
                {trackedBillsToShow.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {trackedBillsToShow.map((bill: any) => {
                      const billNumber = bill.bill_id || bill.number;
                      const billTitle = bill.title || bill.concerning;
                      const workspaces = bill.tracked_by_workspaces || [];
                      const workspaceTooltip = isOrgScope && workspaces.length > 0 
                        ? `Tracked by: ${workspaces.join(', ')}`
                        : undefined;
                      
                      return (
                        <div key={billNumber} className="flex items-center gap-1">
                          {onBillClick ? (
                            <button
                              onClick={() => onBillClick(billNumber)}
                              className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors text-sm"
                              title={workspaceTooltip}
                            >
                              {billNumber}
                              {isOrgScope && workspaces.length > 0 && (
                                <span className="ml-1 text-xs opacity-70">
                                  ({workspaces.length === 1 ? workspaces[0] : `${workspaces.length} workspaces`})
                                </span>
                              )}
                            </button>
                          ) : (
                            <Link
                              to={getBillLink(billNumber)}
                              className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-md hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors text-sm"
                              title={workspaceTooltip}
                            >
                              {billNumber}
                              {isOrgScope && workspaces.length > 0 && (
                                <span className="ml-1 text-xs opacity-70">
                                  ({workspaces.length === 1 ? workspaces[0] : `${workspaces.length} workspaces`})
                                </span>
                              )}
                            </Link>
                          )}
                          {/* Per-bill calendar button */}
                          <AddToCalendarDropdown
                            event={{
                              eventId: event.calendar_id,
                              title: event.reading_title || 'Legislative Event',
                              date: event.reading_date,
                              time: event.reading_time,
                              location: event.room_number,
                              chamber: event.chamber,
                              noTime: event.no_time,
                              uponAdjournment: event.upon_adjournment,
                              billNumbers: [billNumber],
                              billNumber: billNumber,
                              billTitle: billTitle,
                            }}
                            size="sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : isScopedView ? (
                  // Only show "no tracked bills" message for workspace/org scoped views
                  <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary italic">
                    No tracked bills for this event
                  </p>
                ) : null}
              </div>
            )}

            {/* Event Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                  {event.reading_title || 'Legislative Event'}
                </h3>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                  {timeDisplay && (
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {timeDisplay}
                    </div>
                  )}
                  
                  {event.room_number && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      Room {event.room_number}
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <Building className="w-4 h-4 mr-1" />
                    {chamberInfo.label}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 items-center">
                {/* Show derived_location if available, otherwise fall back to old badges */}
                {event.derived_location ? (
                  <Chip 
                    label={event.derived_location} 
                    variant={event.location_type === 'floor' ? 'primary' : 'info'} 
                    size="sm" 
                  />
                ) : (
                  <>
                    {event.joint_meeting && (
                      <Chip label="Joint" variant="warning" size="sm" />
                    )}
                    {(event.committee_flag || isCommitteeEvent(event as any)) && (
                      <Chip label="Committee" variant="info" size="sm" />
                    )}
                  </>
                )}
                
                {/* Notes button - only in authenticated workspace context (not dashboard).
                    Notes and share links key on the legislative calendar_id; a row
                    without one (e.g. an authored event missing event_source) gets
                    neither rather than crashing the whole day view (#1452). */}
                {!isPublic && workspaceId && hasCalendarId && (
                  trackedBillsData.length > 0 ? (
                    <EventNotesButton
                      trackedBills={trackedBillsData}
                      calendarId={String(event.calendar_id)}
                      calendarDisplayName={`${new Date(event.reading_date).toLocaleDateString()}${event.reading_time ? ' ' + formatTimeStringForDisplay(event.reading_time) : ''} - ${event.reading_title}`}
                      workspaceId={workspaceId}
                    />
                  ) : (
                    <CalendarOnlyNotesButton
                      calendarId={String(event.calendar_id)}
                      calendarDisplayName={`${new Date(event.reading_date).toLocaleDateString()}${event.reading_time ? ' ' + formatTimeStringForDisplay(event.reading_time) : ''} - ${event.reading_title}`}
                      workspaceId={workspaceId}
                    />
                  )
                )}
                
                {/* Share event link button - on all authenticated pages including dashboard */}
                {!isPublic && hasCalendarId && (
                  <button
                    onClick={() => copyEventShareLink(event.calendar_id)}
                    className="inline-flex items-center justify-center p-1.5 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                    title="Copy public event link"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                )}
                
                {/* Note: Calendar download buttons are bill-based only, never on event cards. 
                    See .cursor/rules/calendar-display-rules.mdc for rules. */}
              </div>
            </div>

            {/* Description */}
            {event.calendar_description && (
              <div className="mb-4">
                <div className="text-text-secondary dark:text-text-dark-secondary prose prose-sm max-w-none">
                  {linkifyBillNumbers(event.calendar_description, isPublic, onBillClick)}
                </div>
              </div>
            )}

            {/* Bills section for public view (at bottom) */}
            {isPublic && event.bills && event.bills.length > 0 && (
              <div className="border-t border-border-light dark:border-border-dark pt-3">
                <div className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary mb-2 uppercase">
                  Bills ({event.bills.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {event.bills.map((bill, idx) => (
                    <div key={`${bill.number}-${idx}`} className="flex items-center gap-1">
                      <Link
                        to={getBillLink(bill.number)}
                        className={`inline-flex items-center px-2 py-1 text-sm rounded transition-colors ${
                          bill.is_tracked
                            ? 'bg-primary-100 text-primary-800 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 font-medium'
                            : 'bg-background-secondary text-text-secondary hover:bg-background-tertiary dark:bg-background-dark-tertiary dark:text-text-dark-secondary dark:hover:bg-background-dark-secondary'
                        }`}
                      >
                        {bill.number}
                        {bill.is_tracked && <span className="ml-1 text-xs">★</span>}
                      </Link>
                      {/* Per-bill calendar button - only for tracked bills in scoped views, all bills otherwise */}
                      {(!isScopedView || bill.is_tracked) && (
                        <AddToCalendarDropdown
                          event={{
                            eventId: event.calendar_id,
                            title: event.reading_title || 'Legislative Event',
                            date: event.reading_date,
                            time: event.reading_time,
                            location: event.room_number,
                            chamber: event.chamber,
                            noTime: event.no_time,
                            uponAdjournment: event.upon_adjournment,
                            billNumbers: [bill.number],
                            billNumber: bill.number,
                            billTitle: bill.title || bill.concerning,
                          }}
                          size="sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All bills section for workspace context (non-tracked bills) */}
            {!isPublic && workspaceId && allBillNumbers.length > trackedBillsToShow.length && (
              <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                <h4 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary mb-2">
                  All Bills on Calendar ({allBillNumbers.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {allBillNumbers.map((billNumber: string) => (
                    onBillClick ? (
                      <button
                        key={billNumber}
                        onClick={() => onBillClick(billNumber)}
                        className="px-2 py-1 bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary rounded-md hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors text-xs"
                      >
                        {billNumber}
                      </button>
                    ) : (
                      <Link
                        key={billNumber}
                        to={getBillLink(billNumber)}
                        className="px-2 py-1 bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary rounded-md hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors text-xs"
                      >
                        {billNumber}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Agenda Link */}
            {event.agenda_url && (
              <div className="mt-3">
                <a
                  href={event.agenda_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  View Agenda
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        );
      })}
      
      {sortedEvents.length === 0 && (
        <div className="card py-12 text-center text-text-tertiary dark:text-text-dark-tertiary">
          No events found for this date.
        </div>
      )}
      {detailsEvent && (
        <EventDetailsModal event={detailsEvent} isOpen onClose={() => setDetailsEvent(null)} />
      )}
      {trackingEvent && (
        <EventWorkspaceSelectionModal
          eventKey={trackingEvent.key}
          eventTitle={trackingEvent.title}
          isOpen
          onClose={() => setTrackingEvent(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Table View Component (Bill-Centric for both Authenticated and Public)
// ============================================================================

const TableView: React.FC<{
  events: CalendarEventDisplay[];
  isPublic: boolean;
  workspaceId?: string;
  scope?: 'all' | 'org' | 'ws';
  onBillClick?: (billNumber: string) => void;
  dayData?: { date: string; events: any[] };
  /** Show Date column - used for week/month table views */
  showDateColumn?: boolean;
}> = ({ events, isPublic, workspaceId, scope, onBillClick, dayData, showDateColumn = false }) => {
  // Default sort: date ascending for month/week views, time ascending for day view
  const [sortBy, setSortBy] = useState<SortField>(showDateColumn ? 'date' : 'time');
  const [sortDirection, setSortDirection] = useState<SortDirection>(showDateColumn ? 'asc' : 'asc');

  // Get bill link path
  const getBillLink = (billNumber: string) => {
    return isPublic 
      ? `/public/bills/${encodeURIComponent(billNumber)}`
      : `/legislation/${encodeURIComponent(billNumber)}`;
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-primary-600" /> : 
      <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  // Sortable header (omit default px-5 when caller provides pl-/pr-/px- so Time/Bill padding applies)
  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ 
    field, children, className = '' 
  }) => {
    const hasCustomPadding = /\b(px|pl|pr)-/.test(className);
    return (
    <th 
      className={`text-left py-4 ${hasCustomPadding ? '' : 'px-5'} font-medium text-text-secondary dark:text-text-dark-secondary text-sm cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary select-none border-r border-border-light dark:border-border-dark ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {getSortIcon(field)}
      </div>
    </th>
  );
  };

  // Determine if we're in a scoped view (org or workspace level)
  const isScopedView = scope === 'org' || scope === 'ws' || !!workspaceId;
  const isOrgScope = scope === 'org';

  // Always use bill-centric table view
  // Column order: Time | Bill | Title | Sponsors | Status | Room | [Workspace for org] | [Notes for authenticated] | Actions
  // For scoped views: show only tracked bills
  // For "all" scope: show all bills
  interface BillRow {
      bill_id: string;
      bill_number: string;
      bill_db_id: string;
      title: string;
      concerning?: string;
      status: string;
      sponsors: any[];
      bill_order: number;
      event_id: number;
      event_display_order: number;
      reading_date: string;
      reading_time?: string;
      no_time: boolean;
      upon_adjournment: boolean;
      upon_adjournment_of_committee_hearing?: boolean;
      calendar_description?: string;
      room_number?: string;
      reading_title: string;
      derived_location?: string;
      location_type?: 'floor' | 'committee';
      tracked_by_workspaces?: string[];
      chamber?: string;
    }

    const billRows: BillRow[] = [];

    // Use dayData for authenticated view, events for public view
    const eventsToProcess = dayData?.events || events;

    // Process each event and extract bills
    eventsToProcess.forEach((event: any) => {
      // Determine which bills to show based on context:
      // - Authenticated with workspace: use tracked_bills from backend
      // - Public scoped (org/ws): filter bills by is_tracked flag
      // - Public "all" scope or no workspace: show all bills
      let billsToShow: any[] = [];
      
      if (!isPublic && workspaceId) {
        // Authenticated workspace view - use tracked_bills only (no fallback)
        // tracked_bills contains full bill details from backend
        billsToShow = event.tracked_bills || [];
      } else if (isPublic && isScopedView) {
        // Public scoped view (org/ws) - filter bills by is_tracked
        billsToShow = (event.bills || []).filter((b: any) => b.is_tracked);
      } else {
        // Public "all" scope or authenticated without workspace (dashboard) - show all bills
        billsToShow = event.bills || [];
        
        // Fallback for dashboard: if no bills array but we have bill_numbers, create minimal bill objects
        // This handles the calendar list API which may only return bill_numbers
        if (billsToShow.length === 0 && event.bill_numbers && event.bill_numbers.length > 0) {
          billsToShow = event.bill_numbers.map((billNum: string, idx: number) => ({
            bill_id: billNum,
            number: billNum,
            bill_order: idx
          }));
        }
      }
      
      if (billsToShow.length > 0) {
        billsToShow.forEach((bill: any) => {
          const dbId = bill.bill_db_id || bill.id || '';
          billRows.push({
            bill_id: bill.bill_id || bill.number || '',
            bill_number: bill.bill_id || bill.number || '',
            bill_db_id: dbId ? String(dbId) : '',
            title: bill.title || bill.concerning || '',
            concerning: bill.concerning,
            status: bill.status || '',
            sponsors: bill.sponsors || [],
            bill_order: bill.bill_order || 0,
            event_id: event.calendar_id,
            event_display_order: (event as any).display_order ?? 0,
            reading_date: event.reading_date,
            reading_time: event.reading_time,
            no_time: event.no_time || false,
            upon_adjournment: event.upon_adjournment || false,
            upon_adjournment_of_committee_hearing: event.upon_adjournment_of_committee_hearing || false,
            calendar_description: event.calendar_description,
            room_number: event.room_number,
            reading_title: event.reading_title,
            derived_location: event.derived_location,
            location_type: event.location_type,
            tracked_by_workspaces: bill.tracked_by_workspaces || [],
            chamber: event.chamber
          });
        });
      } else {
        // Event has no bills — still show it as a row so non-bill events are visible
        billRows.push({
          bill_id: '',
          bill_number: '',
          bill_db_id: '',
          title: event.reading_title || '',
          concerning: undefined,
          status: '',
          sponsors: [],
          bill_order: 0,
          event_id: event.calendar_id,
          event_display_order: (event as any).display_order ?? 0,
          reading_date: event.reading_date,
          reading_time: event.reading_time,
          no_time: event.no_time || false,
          upon_adjournment: event.upon_adjournment || false,
          upon_adjournment_of_committee_hearing: event.upon_adjournment_of_committee_hearing || false,
          calendar_description: event.calendar_description,
          room_number: event.room_number,
          reading_title: event.reading_title,
          derived_location: event.derived_location,
          location_type: event.location_type,
          tracked_by_workspaces: [],
          chamber: event.chamber
        });
      }
    });

    // Deduplicate: same bill on the same date should appear once, under the most
    // specific event (e.g. "Conference Committee on HB26-1380" beats the JBC
    // umbrella event or a floor "Conference Committees to Report" listing).
    // Bills on different dates are kept (committee hearing vs floor reading).
    const seen = new Map<string, number>();
    const toRemove = new Set<number>();

    const specificityScore = (r: BillRow) => {
      let s = 0;
      if (r.location_type === 'committee') s += 2;
      if (r.bill_number && r.reading_title?.includes(r.bill_number)) s += 4;
      return s;
    };

    billRows.forEach((row, idx) => {
      if (!row.bill_number) return;
      const dateStr = (row.reading_date || '').split('T')[0];
      const key = `${row.bill_number}|${dateStr}`;

      if (!seen.has(key)) {
        seen.set(key, idx);
        return;
      }

      const existingIdx = seen.get(key)!;
      if (specificityScore(row) > specificityScore(billRows[existingIdx])) {
        toRemove.add(existingIdx);
        seen.set(key, idx);
      } else {
        toRemove.add(idx);
      }
    });

    if (toRemove.size > 0) {
      const kept = billRows.filter((_, idx) => !toRemove.has(idx));
      billRows.length = 0;
      billRows.push(...kept);
    }

    // Apply sorting
    billRows.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date': {
          // Sort by date, chamber (Senate first), event display_order (source order), then bill_order
          // display_order captures the exact order from the legislative calendar
          const dateA = (a.reading_date || '').split('T')[0];
          const dateB = (b.reading_date || '').split('T')[0];
          comparison = dateA.localeCompare(dateB);
          if (comparison !== 0) break;
          
          // Chamber first: Senate before House (matching bill sorting convention)
          const chamberA = (a.chamber || '').toLowerCase();
          const chamberB = (b.chamber || '').toLowerCase();
          if (chamberA.includes('senate') && !chamberB.includes('senate')) { comparison = -1; break; }
          if (!chamberA.includes('senate') && chamberB.includes('senate')) { comparison = 1; break; }
          
          // Within chamber, use event's display_order (source order from legislative calendar)
          comparison = (a.event_display_order ?? 0) - (b.event_display_order ?? 0);
          if (comparison !== 0) break;
          
          // Final tiebreaker: bill_order within the event
          comparison = (a.bill_order || 0) - (b.bill_order || 0);
          break;
        }
        case 'billNumber':
          comparison = a.bill_number.localeCompare(b.bill_number);
          break;
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'room':
        case 'location':
          comparison = (a.derived_location || '').localeCompare(b.derived_location || '');
          if (comparison !== 0) break;
          comparison = (a.room_number || '').localeCompare(b.room_number || '');
          break;
        case 'time': {
          // Chamber first: Senate before House
          const chamberA = (a.chamber || '').toLowerCase();
          const chamberB = (b.chamber || '').toLowerCase();
          if (chamberA.includes('senate') && !chamberB.includes('senate')) { comparison = -1; break; }
          if (!chamberA.includes('senate') && chamberB.includes('senate')) { comparison = 1; break; }
          // Within chamber, use event's display_order (source order from legislative calendar)
          comparison = (a.event_display_order ?? 0) - (b.event_display_order ?? 0);
          if (comparison !== 0) break;
          comparison = (a.bill_order || 0) - (b.bill_order || 0);
          break;
        }
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = a.bill_order - b.bill_order;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Format date for display in week/month views
    const formatDateForDisplay = (dateStr: string) => {
      if (!dateStr) return '-';
      // Handle both date-only strings (2025-05-05) and full ISO timestamps (2025-05-05T00:00:00.000Z)
      const dateOnly = dateStr.split('T')[0];
      const date = new Date(dateOnly + 'T00:00:00');
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    return (
      <div>
        <StandardTable horizontalScroll>
          <StandardTableHeader>
            <tr>
              {/* Date column for week/month views */}
              {showDateColumn && (
                <SortableHeader field="date" className="min-w-[72px] py-4 pl-2.5 pr-1">Date</SortableHeader>
              )}
              <SortableHeader field="time" className="min-w-[72px] py-4 pl-2.5 pr-1">Time</SortableHeader>
              <SortableHeader field="billNumber" className="min-w-[92px] py-4 pl-2.5 pr-1">Bill</SortableHeader>
              <SortableHeader field="title" className="min-w-[200px]">Title</SortableHeader>
              <th className="text-left py-4 px-5 font-medium text-text-secondary dark:text-text-dark-secondary text-sm min-w-[180px] border-r border-border-light dark:border-border-dark">Reading</th>
              <SortableHeader field="location" className="min-w-[140px]">Location</SortableHeader>
              <th className="text-left py-4 px-5 font-medium text-text-secondary dark:text-text-dark-secondary text-sm min-w-[180px] border-r border-border-light dark:border-border-dark">Sponsors</th>
              <SortableHeader field="status" className="min-w-[200px]">Status</SortableHeader>
              {/* Workspace column for org-level reports */}
              {isOrgScope && (
                <th className="text-left py-4 px-5 font-medium text-text-secondary dark:text-text-dark-secondary text-sm border-r border-border-light dark:border-border-dark">Workspace</th>
              )}
              {/* Notes column only for authenticated views with workspace */}
              {!isPublic && workspaceId && (
                <th className="text-left py-4 px-5 font-medium text-text-secondary dark:text-text-dark-secondary text-sm border-r border-border-light dark:border-border-dark">Notes</th>
              )}
              <th className="text-right py-4 px-5 font-medium text-text-secondary dark:text-text-dark-secondary text-sm">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp
                    tone="default"
                    glossaryLink={!isPublic}
                    icons={
                      isPublic
                        ? ['add-to-calendar']
                        : ['add-to-calendar', 'public-link']
                    }
                  />
                </span>
              </th>
            </tr>
          </StandardTableHeader>
          <tbody>
            {billRows.map((row, idx) => {
              const timeDisplay = formatTimeDisplay({
                reading_time: row.reading_time,
                no_time: row.no_time,
                upon_adjournment: row.upon_adjournment,
                upon_adjournment_of_committee_hearing: row.upon_adjournment_of_committee_hearing
              });
              const timeShort = timeDisplay === 'Upon Adjournment' ? 'Upon Adj.' : timeDisplay;
              
              return (
                <tr 
                  key={`${row.bill_number}-${row.event_id}-${idx}`}
                  className="border-b border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary"
                >
                  {/* Date column for week/month views */}
                  {showDateColumn && (
                    <td className="py-4 pl-2.5 pr-1 text-sm text-text-primary dark:text-text-dark-primary whitespace-nowrap border-r border-border-light dark:border-border-dark">
                      {formatDateForDisplay(row.reading_date)}
                    </td>
                  )}
                  <td className="py-4 pl-2.5 pr-1 text-sm text-text-primary dark:text-text-dark-primary whitespace-nowrap border-r border-border-light dark:border-border-dark">
                    {timeShort}
                  </td>
                  <td className="py-4 pl-2.5 pr-1 whitespace-nowrap border-r border-border-light dark:border-border-dark">
                    {row.bill_number ? (
                      onBillClick ? (
                        <button
                          onClick={() => onBillClick(row.bill_number)}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {row.bill_number}
                        </button>
                      ) : (
                        <Link
                          to={getBillLink(row.bill_number)}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {row.bill_number}
                        </Link>
                      )
                    ) : (
                      <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">-</span>
                    )}
                  </td>
                  <td className="py-4 px-5 text-sm text-text-primary dark:text-text-dark-primary max-w-md border-r border-border-light dark:border-border-dark">
                    {row.title || row.concerning || '-'}
                  </td>
                  {/* Reading column - shows reading_title */}
                  <td className="py-4 px-5 text-sm text-text-secondary dark:text-text-dark-secondary min-w-[150px] max-w-[250px] border-r border-border-light dark:border-border-dark">
                    <div className="line-clamp-2" title={row.reading_title || '-'}>
                      {row.reading_title || '-'}
                    </div>
                  </td>
                  {/* Location + Room combined: location on first row, room on second */}
                  <td className="py-4 px-5 text-sm text-text-secondary dark:text-text-dark-secondary min-w-[120px] max-w-[200px] border-r border-border-light dark:border-border-dark">
                    <div className="flex flex-col gap-0.5" title={`${row.derived_location || '-'}${row.room_number ? ` • ${row.room_number}` : ''}`}>
                      <span className="line-clamp-1">{row.derived_location || '-'}</span>
                      <span className="text-text-tertiary dark:text-text-dark-tertiary line-clamp-1">{row.room_number || '-'}</span>
                    </div>
                  </td>
                  <td className="py-4 px-5 text-sm border-r border-border-light dark:border-border-dark">
                    {row.sponsors && row.sponsors.length > 0 ? (
                      <SponsorDisplay sponsors={row.sponsors} isPublic={isPublic} billNumber={row.bill_number} />
                    ) : (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">-</span>
                    )}
                  </td>
                  <td className="py-4 px-5 text-sm text-text-secondary dark:text-text-dark-secondary border-r border-border-light dark:border-border-dark">
                    {row.status || '-'}
                  </td>
                  {/* Workspace column for org-level reports */}
                  {isOrgScope && (
                    <td className="py-4 px-5 text-sm text-text-secondary dark:text-text-dark-secondary border-r border-border-light dark:border-border-dark">
                      {row.tracked_by_workspaces && row.tracked_by_workspaces.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.tracked_by_workspaces.map((ws, wsIdx) => (
                            <span
                              key={wsIdx}
                              className="inline-block px-2 py-0.5 text-xs bg-background-tertiary dark:bg-background-dark-tertiary rounded"
                              title={ws}
                            >
                              {ws.length > 15 ? ws.substring(0, 15) + '...' : ws}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-text-tertiary dark:text-text-dark-tertiary">-</span>
                      )}
                    </td>
                  )}
                  {/* Notes column only for authenticated views with workspace */}
                  {!isPublic && workspaceId && (
                    <td className="py-4 px-5 border-r border-border-light dark:border-border-dark">
                      {/* Notes are a legislative-event feature; authored events
                          (workspace/public) have no calendar_id → no event_id */}
                      {row.event_id != null && (
                        <TableNotesButton
                          billId={row.bill_db_id}
                          billNumber={row.bill_number}
                          billTitle={row.title}
                          calendarId={row.event_id.toString()}
                          calendarDisplayName={row.reading_title}
                          workspaceId={workspaceId}
                        />
                      )}
                    </td>
                  )}
                  <td className="py-4 px-5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* ICS download + public share link are per-legislative-event
                          endpoints (integer ids) — hide for authored events */}
                      {row.event_id != null && (
                      <AddToCalendarDropdown
                        event={{
                          eventId: row.event_id,
                          title: row.reading_title || 'Legislative Event',
                          date: row.reading_date,
                          time: row.reading_time,
                          location: row.room_number,
                          chamber: row.chamber,
                          noTime: row.no_time,
                          uponAdjournment: row.upon_adjournment,
                          billNumbers: [row.bill_number],
                          billNumber: row.bill_number,
                          billTitle: row.title || row.concerning,
                        }}
                        size="sm"
                      />
                      )}
                      {!isPublic && row.event_id != null && (
                        <button
                          onClick={() => copyEventShareLink(row.event_id)}
                          className="p-1.5 text-text-tertiary hover:text-primary-600 dark:text-text-dark-tertiary dark:hover:text-primary-400 transition-colors"
                          title="Copy public event link"
                          aria-label="Copy public event link"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </StandardTable>

        {billRows.length === 0 && (
          <div className="py-12 text-center text-text-tertiary dark:text-text-dark-tertiary">
            {isScopedView ? 'No tracked bills found for this date.' : 'No events on the calendar for this date.'}
          </div>
        )}
      </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Unified CalendarDayView component
 * 
 * Used by both authenticated and public calendar pages.
 * Any changes made here will automatically reflect in both contexts.
 */
const CalendarDayView: React.FC<CalendarDayViewProps> = ({
  events,
  viewMode,
  isPublic = false,
  workspaceId,
  scope,
  onBillClick,
  dayData,
  showDateColumn = false,
}) => {
  // Batch note counts for the whole view (#1179); see CalendarNoteCountsContext.
  const notesEnabled = !isPublic && !!workspaceId;
  const allEvents = React.useMemo(
    () => [...events, ...((dayData?.events || []) as CalendarEventDisplay[])],
    [events, dayData]
  );
  const calendarIds = React.useMemo(
    () => allEvents.map((e) => String((e as any).calendar_id ?? '')).filter(Boolean),
    [allEvents]
  );
  const billIds = React.useMemo(
    () =>
      allEvents.flatMap((e) =>
        (((e as any).tracked_bills || []) as any[]).map((b) => String(b.bill_db_id || b.id || ''))
      ).filter(Boolean),
    [allEvents]
  );
  const calendarCounts = useEntityNoteCounts('calendar', calendarIds, workspaceId, notesEnabled);
  const billCounts = useEntityNoteCounts('bill', billIds, workspaceId, notesEnabled);
  const noteCounts = React.useMemo<CalendarNoteCounts | null>(
    () => (notesEnabled ? { calendar: calendarCounts ?? {}, bill: billCounts ?? {} } : null),
    [notesEnabled, calendarCounts, billCounts]
  );

  const view =
    viewMode === 'card' ? (
      <CardView
        events={events}
        isPublic={isPublic}
        workspaceId={workspaceId}
        scope={scope}
        onBillClick={onBillClick}
      />
    ) : (
      <TableView
        events={events}
        isPublic={isPublic}
        workspaceId={workspaceId}
        scope={scope}
        onBillClick={onBillClick}
        dayData={dayData}
        showDateColumn={showDateColumn}
      />
    );

  return <CalendarNoteCountsContext.Provider value={noteCounts}>{view}</CalendarNoteCountsContext.Provider>;
};

export default CalendarDayView;

